import { CampaignMetric } from '@/shared/types/campaigns';

interface MetricLabels {
  labelKey: string;
  tooltipKey?: string;
}

/**
 * Label and tooltip for every metric key the page renders. Values print bare; currency tiles
 * get the campaign's own currency code from the caller.
 */
const METRIC_LABELS: Record<string, MetricLabels> = {
  debt_amount: {
    labelKey: 'campaigns.performance.metrics.debt_amount',
    tooltipKey: 'campaigns.performance.tooltips.debt_amount',
  },
  total_ptp_count: {
    labelKey: 'campaigns.performance.metrics.total_ptp_count',
    tooltipKey: 'campaigns.performance.tooltips.total_ptp_count',
  },
  total_ptp_amount: {
    labelKey: 'campaigns.performance.metrics.total_ptp_amount',
    tooltipKey: 'campaigns.performance.tooltips.total_ptp_amount',
  },
  ppa: {
    labelKey: 'campaigns.performance.metrics.ppa',
    tooltipKey: 'campaigns.performance.tooltips.ppa',
  },
  promises: {
    labelKey: 'campaigns.performance.metrics.promises',
    tooltipKey: 'campaigns.performance.tooltips.promises',
  },
  disputes: {
    labelKey: 'campaigns.performance.metrics.disputes',
    tooltipKey: 'campaigns.performance.tooltips.disputes',
  },
  dispute_rate: {
    labelKey: 'campaigns.performance.metrics.dispute_rate',
    tooltipKey: 'campaigns.performance.tooltips.dispute_rate',
  },
  stopped: {
    labelKey: 'campaigns.performance.metrics.stopped',
    tooltipKey: 'campaigns.performance.tooltips.stopped',
  },
};

/**
 * Dropped: the funnel already shows all three. `rpc` is the verified-debtors stage,
 * `rpc_rate` and `ptp_rate` are the rate pills on the segments leading into it.
 */
const SUPPRESSED_METRICS: ReadonlySet<string> = new Set(['rpc', 'rpc_rate', 'ptp_rate']);

// Render order: the three currency tiles fill row one at `lg`, the counts wrap onto row two.
const RESULT_TILE_KEYS = ['debt_amount', 'total_ptp_amount', 'ppa', 'total_ptp_count'];
const DISPUTE_KEYS = ['disputes', 'dispute_rate'];
const EXCLUSION_KEYS = ['stopped'];
const PROMISED_DEBTS_KEY = 'promises';

const PLACED_KEYS: ReadonlySet<string> = new Set([
  ...RESULT_TILE_KEYS,
  ...DISPUTE_KEYS,
  ...EXCLUSION_KEYS,
  PROMISED_DEBTS_KEY,
]);

export interface ResolvedMetric extends CampaignMetric {
  labelKey: string;
  tooltipKey?: string;
}

export interface GroupedMetrics {
  /** Ordered: debt_amount, total_ptp_amount, ppa, total_ptp_count. */
  resultTiles: ResolvedMetric[];
  /** `promises` — debts with a promise to pay. Null when absent. */
  promisedDebts: ResolvedMetric | null;
  /** Ordered: disputes, dispute_rate. */
  disputes: ResolvedMetric[];
  /** Ordered: stopped. */
  exclusions: ResolvedMetric[];
  /** Unrecognised keys, payload order, labelled with the raw key. */
  leftover: ResolvedMetric[];
}

/**
 * Assigns each metric to its section slot in a fixed order, ignoring payload order. A key the
 * payload omits is simply absent; an unrecognised key falls into `leftover` rather than vanishing.
 */
export const groupMetrics = (metrics: CampaignMetric[] = []): GroupedMetrics => {
  const byKey = new Map<string, CampaignMetric>();
  metrics.forEach((metric) => {
    if (!byKey.has(metric.key)) {
      byKey.set(metric.key, metric);
    }
  });

  const resolve = (key: string): ResolvedMetric | null => {
    const metric = byKey.get(key);
    if (!metric) {
      return null;
    }
    return { ...metric, ...METRIC_LABELS[key] };
  };

  const resolveAll = (keys: string[]): ResolvedMetric[] =>
    keys.map(resolve).filter((metric): metric is ResolvedMetric => metric !== null);

  const leftover = metrics
    .filter((metric) => !PLACED_KEYS.has(metric.key) && !SUPPRESSED_METRICS.has(metric.key))
    .map((metric) => ({ ...metric, labelKey: metric.key }));

  return {
    resultTiles: resolveAll(RESULT_TILE_KEYS),
    promisedDebts: resolve(PROMISED_DEBTS_KEY),
    disputes: resolveAll(DISPUTE_KEYS),
    exclusions: resolveAll(EXCLUSION_KEYS),
    leftover,
  };
};

/** Reads one metric's raw value out of the payload, for cross-metric maths. */
export const findMetricValue = (metrics: CampaignMetric[] = [], key: string): number | null =>
  metrics.find((metric) => metric.key === key)?.value ?? null;

export const FUNNEL_STAGE_LABELS: Record<string, string> = {
  attempts: 'campaigns.performance.funnel.attempts',
  responded: 'campaigns.performance.funnel.responded',
  right_party_contacts: 'campaigns.performance.funnel.right_party_contacts',
  promises: 'campaigns.performance.funnel.promises',
};

/** Rate pill labels, keyed by the stage the rate leads into. `attempts` has none. */
export const FUNNEL_RATE_LABELS: Record<string, string> = {
  responded: 'campaigns.performance.funnel_rates.hit_rate',
  right_party_contacts: 'campaigns.performance.funnel_rates.rpc_rate',
  promises: 'campaigns.performance.funnel_rates.ptp_rate',
};
